import { revealSpoiler } from '@engineer-os/engine';

import { getLocale, isLocale, type Locale } from './locale';

/**
 * The debrief: the long-form article a learner reads after clearing a level.
 *
 * One level's article is one fetch, made when the learner opens it. The whole
 * set is a third of a megabyte and lives in the Worker (worker/articles.json),
 * not in `missions.json` — a catalogue that carried fifty walkthroughs would
 * both triple in size on the way to the map and hand every level's answer to
 * anybody who opened the file.
 *
 * The rung is 6 because rungs 1-5 are the hint ladder and 0 is the reflection
 * (packages/engine/src/spoilers.ts). As everywhere else in this product the
 * obfuscation is not security — the key is in this bundle — it is so that the
 * network tab does not spoil a level nobody has played yet. The real gate is
 * the Worker's, which never sends a paid level's article to a browser that has
 * not bought it, and the progress record's, which is what decides whether the
 * screen offers to show one at all.
 *
 * ## Two languages
 *
 * `?lang=<locale>` asks for the translated half, which the Worker answers from
 * `worker/articles.<locale>.json` — the same shape, the same spoiler key (the
 * id and the rung, neither of which is translated), the same three refusals.
 * English is the source language and asks for nothing.
 *
 * An untranslated level answers 404 to `?lang=ar`, and a 404 is where the
 * fallback lives: the English article, rather than a screen saying the course
 * is not finished. Fifty levels are translated one at a time and a half
 * translated course has to read as a course. `ready` therefore carries the
 * language it actually got, because the screen has to set `dir` on the prose
 * and count its words at that language's pace.
 */
const ARTICLE_RUNG = 6;

/**
 * `?lang=` and not a path segment, an `Accept-Language` header or a cookie.
 *
 * The query string is part of the cache key at every layer that sits between
 * this and the Worker — the browser's, the edge's, a corporate proxy's —
 * without any of them being configured to vary on anything. A header would
 * have to be varied on and would be got wrong exactly once, in front of every
 * reader of one language, which is the kind of bug that arrives as "the site
 * is in Arabic for everybody now".
 */
const url = (id: string, locale: Locale): string => {
  const path = `/api/articles/${encodeURIComponent(id)}`;
  return locale === 'en' ? path : `${path}?lang=${encodeURIComponent(locale)}`;
};

/**
 * What came back. A refusal is a state rather than a failure, the way
 * `unlockPaid` treats one: "nobody is signed in", "this account has not bought
 * the course" and "there is no Worker in front of this page" are all ordinary
 * things for the screen to be told, and each of them says something different
 * to the reader.
 */
export type ArticleResult =
  /** `locale` is the language of `text`, which is not always the one asked for. */
  | { state: 'ready'; text: string; locale: Locale }
  /** No article for this level in any language, or no such level. */
  | { state: 'missing' }
  | { state: 'anonymous' }
  | { state: 'unowned' }
  /** Offline, or a build served without the Worker behind it. */
  | { state: 'unavailable' };

/**
 * Cached for the visit, keyed by **language and id**, never by id alone.
 *
 * The id alone is the one bug this module can have that nobody notices in
 * English: a reader opens a debrief, switches to Arabic, and is handed back
 * the English text they already downloaded — from a cache that was asked
 * "have we fetched l07?" when the question is "have we fetched l07 in
 * Arabic?". `en:l07-x` and `ar:l07-x` are different requests to different
 * files and they are different entries here.
 *
 * The promise is cached rather than the result, so a reader who opens the same
 * debrief twice in one visit — or a component that mounts, unmounts and
 * remounts while the request is still going — makes one request. Only outcomes
 * that would not change on a retry are kept: a failure is evicted, so a reader
 * who signs in, or whose connection comes back, gets a real answer the next
 * time they ask rather than a cached "no".
 */
const cache = new Map<string, Promise<ArticleResult>>();

const key = (id: string, locale: Locale) => `${locale}:${id}`;

export function fetchArticle(id: string, locale: Locale = getLocale()): Promise<ArticleResult> {
  const k = key(id, locale);
  const hit = cache.get(k);
  if (hit) return hit;

  const pending = load(id, locale).then((result) => {
    if (result.state !== 'ready' && result.state !== 'missing') cache.delete(k);
    return result;
  });
  cache.set(k, pending);
  return pending;
}

/**
 * The asked-for language, then English.
 *
 * Only `missing` falls back. A 401 or a 402 is about this account and not
 * about this language, and retrying them in English would ask a reader who
 * has not bought the course to be told the price twice.
 */
async function load(id: string, locale: Locale): Promise<ArticleResult> {
  const asked = await request(id, locale);
  if (asked.state === 'missing' && locale !== 'en') return request(id, 'en');
  return asked;
}

async function request(id: string, locale: Locale): Promise<ArticleResult> {
  let res: Response;
  try {
    // `same-origin`: a paid article is served against the session cookie, the
    // same way the paid missions are.
    res = await fetch(url(id, locale), { credentials: 'same-origin' });
  } catch {
    return { state: 'unavailable' };
  }

  if (res.status === 404) return { state: 'missing' };
  if (res.status === 401) return { state: 'anonymous' };
  if (res.status === 402) return { state: 'unowned' };
  if (!res.ok) return { state: 'unavailable' };

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { state: 'unavailable' };
  }

  const body = raw as { id?: unknown; article?: unknown; locale?: unknown } | null;
  if (!body || typeof body.article !== 'string' || body.article === '') return { state: 'unavailable' };
  // The id is checked rather than trusted: a cache or a proxy that answered
  // one level's request with another level's article would otherwise put the
  // wrong debrief in front of the reader, under the right title, with nothing
  // to say it was wrong.
  if (body.id !== id) return { state: 'unavailable' };

  /**
   * The Worker is free to answer an untranslated level in English rather than
   * 404 — both are correct, and the two halves of this feature are being
   * written at the same time. Either way the body says which language it sent
   * and that is what the screen renders in, so a `locale` that is not the one
   * asked for is information, not a mismatch to reject. An absent one means
   * the English route, which predates all of this.
   */
  const served = isLocale(body.locale) ? body.locale : 'en';

  // The spoiler key is the id and the rung. Neither is translated, so one
  // reveal serves both halves.
  return { state: 'ready', text: revealSpoiler(body.article, id, ARTICLE_RUNG), locale: served };
}
